import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Message, useCopilotStore } from "@/app/lib/stores/useCopilotStore";
import { cn } from "@/app/lib/utils";
import { ChevronDownIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, ClockIcon, ExclamationCircleIcon, FlagIcon } from "@heroicons/react/24/outline";
import { FeedbackModal } from "./FeedbackModal";

interface ChatMessageProps {
  message: Message & { context?: any };
}

export function ChatMessage({ message }: ChatMessageProps) {
  const [showContext, setShowContext] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  const isSystem = message.role === "system";
  
  // Get tool execution if this is a system message with tool_call_id
  const toolExecutions = useCopilotStore((state) => state.toolExecutions);
  const toolExecution = message.tool_call_id ? toolExecutions.get(message.tool_call_id) : null;
  
  // Get current session ID from store
  const sessionId = useCopilotStore((state) => state.sessionId);
  
  // Check if debug mode is enabled
  const isDebugMode = process.env.NEXT_PUBLIC_DEBUG_MODE === 'true';
  
  // Show context toggle only in debug mode and for user messages with context
  const canShowContext = isDebugMode && isUser && message.context;

  if (isSystem) {
    // Determine status and styling based on tool execution
    const getStatusIcon = () => {
      if (!toolExecution) return null;
      
      switch (toolExecution.status) {
        case 'completed':
          return <CheckCircleIcon className="w-3 h-3 text-green-600" />;
        case 'failed':
          if (toolExecution.failure_type === 'timeout') {
            return <ClockIcon className="w-3 h-3 text-orange-600" />;
          } else if (toolExecution.failure_type === 'denied') {
            return <XCircleIcon className="w-3 h-3 text-red-600" />;
          } else {
            return <ExclamationCircleIcon className="w-3 h-3 text-red-600" />;
          }
        default:
          return null;
      }
    };
    
    const getBorderColor = () => {
      if (!toolExecution) return "bg-gray-100 text-gray-600";
      
      switch (toolExecution.status) {
        case 'completed':
          return "bg-green-50 border border-green-200 text-green-700";
        case 'failed':
          if (toolExecution.failure_type === 'timeout') {
            return "bg-orange-50 border border-orange-200 text-orange-700";
          } else {
            return "bg-red-50 border border-red-200 text-red-700";
          }
        default:
          return "bg-gray-100 text-gray-600";
      }
    };
    
    const getStatusText = () => {
      if (!toolExecution) return message.content;
      
      if (toolExecution.status === 'completed') {
        return message.content === 'Denied action' ? 'Denied action' : 'Approved action';
      } else if (toolExecution.status === 'failed') {
        return message.content === 'Denied action' ? 'Denied action' : 'Approved action - Failed';
      }
      return message.content;
    };
    
    return (
      <div className="flex justify-center my-2">
        <div className={cn(
          "text-xs px-3 py-1 rounded-full flex items-center gap-1",
          getBorderColor()
        )}>
          {getStatusIcon()}
          <span>{getStatusText()}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex gap-3",
        isUser && "flex-row-reverse"
      )}
    >
      {/* Message content */}
      <div
        className={cn(
          "flex flex-col gap-1",
          isUser ? "items-end max-w-[70%]" : "max-w-[85%]"
        )}
      >
        {/* Message bubble/text */}
        {isUser ? (
          // User message - subtle gray bubble on right
          <div className="bg-gray-100 rounded-2xl px-4 py-2 text-sm text-gray-800">
            <div className="whitespace-pre-wrap break-words">
              {message.content}
            </div>
          </div>
        ) : (
          // Assistant message - normal text on left with markdown support
          <div 
            className="relative group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Report Button - shows on hover, positioned inside the container */}
            {isAssistant && sessionId && (
              <button
                onClick={() => setShowFeedbackModal(true)}
                className={cn(
                  "absolute -top-1 right-0 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all z-10",
                  "opacity-0 group-hover:opacity-100"
                )}
                title="Report an issue with this response"
              >
                <FlagIcon className="w-4 h-4" />
              </button>
            )}
            
            <div className="prose prose-sm max-w-none text-sm text-gray-800 pr-8">
              <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom table rendering for better responsiveness
                table: ({ children }: any) => (
                  <div className="overflow-x-auto my-3">
                    <table className="min-w-full divide-y divide-gray-200 border-collapse">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }: any) => (
                  <thead className="bg-gray-50">{children}</thead>
                ),
                tbody: ({ children }: any) => (
                  <tbody className="bg-white divide-y divide-gray-200">{children}</tbody>
                ),
                th: ({ children }: any) => (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border border-gray-200">
                    {children}
                  </th>
                ),
                td: ({ children }: any) => (
                  <td className="px-3 py-2 text-sm text-gray-900 border border-gray-200 whitespace-normal">
                    {children}
                  </td>
                ),
                // Custom code rendering
                code: ({ children, ...props }: any) => {
                  // Check if it's an inline code or code block
                  const isInline = !props.node?.position || 
                    (props.node.position.start.line === props.node.position.end.line);
                  
                  return isInline ? (
                    <code className="px-1 py-0.5 bg-gray-100 rounded text-sm font-mono text-gray-800">
                      {children}
                    </code>
                  ) : (
                    <code className="block bg-gray-100 rounded-lg p-3 text-sm font-mono text-gray-800 overflow-x-auto whitespace-pre">
                      {children}
                    </code>
                  );
                },
                pre: ({ children }: any) => (
                  <pre className="bg-gray-100 rounded-lg p-3 overflow-x-auto">
                    {children}
                  </pre>
                ),
                // Custom link rendering to open in new tab
                a: ({ href, children }: any) => (
                  <a 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    {children}
                  </a>
                ),
                // Lists
                ul: ({ children }: any) => (
                  <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>
                ),
                ol: ({ children }: any) => (
                  <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>
                ),
                li: ({ children }: any) => (
                  <li className="ml-2">{children}</li>
                ),
                // Paragraphs
                p: ({ children }: any) => (
                  <p className="my-2 leading-relaxed">{children}</p>
                ),
                // Headings
                h1: ({ children }: any) => (
                  <h1 className="text-xl font-semibold text-gray-900 mt-4 mb-2">{children}</h1>
                ),
                h2: ({ children }: any) => (
                  <h2 className="text-lg font-semibold text-gray-900 mt-3 mb-2">{children}</h2>
                ),
                h3: ({ children }: any) => (
                  <h3 className="text-base font-semibold text-gray-900 mt-2 mb-1">{children}</h3>
                ),
                // Strong/Bold
                strong: ({ children }: any) => (
                  <strong className="font-semibold text-gray-900">{children}</strong>
                ),
                // Blockquote
                blockquote: ({ children }: any) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2">
                    {children}
                  </blockquote>
                ),
                // Horizontal rule
                hr: () => (
                  <hr className="my-4 border-gray-200" />
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
            
            {/* Tools used indicator - only show in debug mode */}
            {isDebugMode && message.tools_used && message.tools_used.length > 0 && (
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                <span className="inline-block w-1 h-1 bg-gray-400 rounded-full" />
                <span>Used: {message.tools_used.join(", ")}</span>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Context debug toggle for user messages */}
        {canShowContext && (
          <button
            onClick={() => setShowContext(!showContext)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mt-1"
          >
            {showContext ? (
              <ChevronDownIcon className="w-3 h-3" />
            ) : (
              <ChevronRightIcon className="w-3 h-3" />
            )}
            <span>Context</span>
          </button>
        )}

        {/* Expandable context view */}
        {showContext && message.context && (
          <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs max-w-full overflow-x-auto">
            <div className="font-medium text-gray-600 mb-1">Context sent with message:</div>
            <pre className="text-gray-600 whitespace-pre-wrap break-words">
              {JSON.stringify(message.context, null, 2)}
            </pre>
          </div>
        )}

        {/* Timestamp - only show for assistant messages with processing time */}
        {isAssistant && message.processing_time_ms && (
          <div className="text-xs text-gray-400 mt-1">
            {(message.processing_time_ms / 1000).toFixed(1)}s
          </div>
        )}
      </div>
      
      {/* Feedback Modal */}
      {isAssistant && sessionId && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          message={message}
          sessionId={sessionId}
        />
      )}
    </div>
  );
}
